import { flexRender } from '@tanstack/react-table';
import type { Table } from '@tanstack/react-table';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { THEAD, TH, TH_STICKY, TH_SORTABLE, TH_PINNED_LEFT } from '../../vacationTable.styles';
import { VacationDpSortableHeader } from './VacationDpSortableHeader';
import { LOCKED_COLUMNS } from '../vacationDpTable.definitions';
import type { VacationDpRow } from '../../../models/vacationDpBoard.types';

export interface VacationDpTableHeadProps {
    table: Table<VacationDpRow>;
    /** Fixa o cabeçalho no topo. Só faz sentido quando a tabela tem rolagem vertical própria. */
    isSticky: boolean;
}

function alignmentClass(align?: 'left' | 'right'): string {
    return align === 'right' ? 'text-right' : 'text-left';
}

function headerAlignmentClass(align?: 'left' | 'right' | 'center'): string {
    if (align === 'center') return 'justify-center';
    if (align === 'right') return 'flex-row-reverse justify-end';
    return 'justify-start';
}

function sortIndicator(sorted: false | 'asc' | 'desc') {
    if (sorted === 'asc') {
        return <ChevronUpIcon className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />;
    }
    if (sorted === 'desc') {
        return <ChevronDownIcon className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />;
    }
    return <ChevronUpDownIcon className="h-3.5 w-3.5 text-raro-gray-300" aria-hidden="true" />;
}

function ariaSort(canSort: boolean, sorted: false | 'asc' | 'desc') {
    if (!canSort) return undefined;
    if (sorted === 'asc') return 'ascending' as const;
    if (sorted === 'desc') return 'descending' as const;
    return 'none' as const;
}

export function VacationDpTableHead({ table, isSticky }: VacationDpTableHeadProps) {
    return (
        <thead className={THEAD}>
            {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                        const meta = (
                            header.column.columnDef.meta as
                                | { align?: 'left' | 'right'; headerAlign?: 'left' | 'right' | 'center' }
                                | undefined
                        );
                        const align = meta?.align;
                        const headerAlign = meta?.headerAlign ?? align;
                        const canSort = header.column.getCanSort();
                        const sorted = header.column.getIsSorted();
                        const isLocked = LOCKED_COLUMNS.includes(header.id as any);
                        const isPinned = header.column.getIsPinned() === 'left';
                        const pinnedLeft = header.column.getStart('left');

                        return (
                            <VacationDpSortableHeader
                                key={header.id}
                                id={header.id}
                                onSort={header.column.getToggleSortingHandler()}
                                canSort={canSort}
                                sorted={sorted}
                                headerAlign={headerAlign}
                                isSticky={isSticky}
                                align={align}
                                isPinned={isPinned}
                                pinnedLeft={pinnedLeft}
                            >
                                {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                            </VacationDpSortableHeader>
                        );
                    })}
                </tr>
            ))}
        </thead>
    );
}

export default VacationDpTableHead;
